import React, { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const MAX_ROWS = 6;
const BADGE = {
  paid: ["Paid", "bg-green-100 text-green-700"],
  overdue: ["Overdue", "bg-red-100 text-red-700"],
  due: ["Due today", "bg-amber-100 text-amber-700"],
  upcoming: ["Upcoming", "bg-slate-100 text-slate-600"],
};

function RowStatus({ r }) {
  const [label, cls] = BADGE[r.status] || BADGE.upcoming;
  const part = r.paid > 0 && r.remaining > 0;
  return (
    <div className="text-right shrink-0">
      <Badge className={cls}>{label}</Badge>
      {part && <p className="text-[11px] text-amber-700 mt-0.5">Part-paid {inr(r.paid)} · {inr(r.remaining)} left</p>}
    </div>
  );
}

export const LumpsumPlanCard = ({ sid, detail, onSaved }) => {
  const cp = detail.collect_plan || { rows: [] };
  const toEdit = (rows) => rows.map((r) => ({ ...r, amount: String(r.amount), date: r.date || "" }));
  const [rows, setRows] = useState(toEdit(cp.rows));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRows(toEdit((detail.collect_plan || { rows: [] }).rows)); }, [detail]);

  const already = cp.rows.reduce((s, r) => s + (r.paid || 0), 0);
  const total = rows.reduce((s, r) => s + (parseInt(r.amount, 10) || 0), 0);
  const unpaidPlanned = Math.max(0, total - already);
  const over = unpaidPlanned > detail.outstanding;
  const setRow = (i, k, v) => setRows((a) => a.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const save = async () => {
    for (const r of rows) {
      if (!(parseInt(r.amount, 10) > 0)) { toast.error("Each row needs an amount"); return; }
      if (!r.date) { toast.error("Each row needs a date"); return; }
    }
    setSaving(true);
    try {
      await api.put(`/fees/students/${sid}/collect-plan`, { rows: rows.map((r) => ({ amount: parseInt(r.amount, 10), date: r.date })) });
      toast.success("Collection plan saved — teachers will see it in Fees to collect");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save plan"); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 space-y-4 min-w-0" data-testid="lumpsum-card">
      <h4 className="font-bold text-slate-900">Lump-sum fee</h4>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-xl bg-slate-50 p-3"><p className="text-[11px] text-slate-500">Total fee</p><p className="font-bold" data-testid="lumpsum-total">{inr(detail.lumpsum?.total_fee)}</p></div>
        <div className="rounded-xl bg-green-50 p-3"><p className="text-[11px] text-slate-500">Paid</p><p className="font-bold text-green-700" data-testid="lumpsum-paid">{inr(detail.lumpsum?.already_paid)}</p></div>
        <div className="rounded-xl bg-red-50 p-3"><p className="text-[11px] text-slate-500">Due</p><p className="font-bold text-red-600" data-testid="lumpsum-due">{inr(detail.outstanding)}</p></div>
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-800">Collection plan</p>
        <p className="text-[11px] text-slate-500">When a row's date arrives, its unpaid amount appears in the teacher's "Collect now". Payments clear the oldest row first.</p>
      </div>
      <div className="space-y-2" data-testid="lumpsum-rows">
        {rows.map((r, i) => {
          const locked = r.status === "paid";
          return (
            <div key={i} className={`flex flex-wrap items-center gap-2 rounded-xl border p-2 ${r.status === "overdue" ? "border-red-200 bg-red-50/40" : "border-slate-200"}`} data-testid={`lumpsum-row-${i}`}>
              <span className="text-xs text-slate-500 shrink-0">Collect</span>
              <Input type="number" inputMode="numeric" value={r.amount} disabled={locked} onChange={(e) => setRow(i, "amount", e.target.value)} className="h-9 w-20 sm:w-24" placeholder="₹" data-testid={`lumpsum-row-amount-${i}`} />
              <span className="text-xs text-slate-500 shrink-0">by</span>
              <Input type="date" value={r.date} disabled={locked} onChange={(e) => setRow(i, "date", e.target.value)} className="h-9 w-36 min-w-0" data-testid={`lumpsum-row-date-${i}`} />
              <div className="hidden sm:block flex-1" />
              {r.status ? <RowStatus r={r} /> : <Badge className="bg-blue-50 text-blue-700">New</Badge>}
              {locked ? <Lock className="h-4 w-4 text-slate-300 shrink-0" title="Paid rows are locked" /> : (
                <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-600 shrink-0" onClick={() => setRows((a) => a.filter((_, j) => j !== i))} data-testid={`lumpsum-row-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
              )}
            </div>
          );
        })}
        {rows.length === 0 && <p className="text-xs text-slate-500" data-testid="lumpsum-empty">No plan yet — teachers see the full due amount ({inr(detail.outstanding)}) as "Collect now".</p>}
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Button type="button" variant="outline" size="sm" disabled={rows.length >= MAX_ROWS} onClick={() => setRows((a) => [...a, { amount: "", date: "" }])} data-testid="lumpsum-add-row"><Plus className="h-4 w-4 mr-1" /> Add row</Button>
        <span className={`text-sm font-semibold ${over ? "text-red-600" : "text-slate-700"}`} data-testid="lumpsum-planned">Planned {inr(unpaidPlanned)} / {inr(detail.outstanding)} due</span>
      </div>
      {over && <p className="text-xs text-red-600" data-testid="lumpsum-over">Unpaid rows can't be more than the amount due.</p>}
      {!over && unpaidPlanned < detail.outstanding && rows.length > 0 && <p className="text-[11px] text-amber-700">{inr(detail.outstanding - unpaidPlanned)} is not scheduled yet.</p>}
      {cp.next && <p className="text-[11px] text-slate-500">Next: {inr(cp.next.amount)}{cp.next.date ? ` by ${formatDate(cp.next.date)}` : ""}</p>}
      <Button onClick={save} disabled={saving || over} className="bg-blue-900 hover:bg-blue-950" data-testid="lumpsum-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save plan"}</Button>
      <p className="text-[11px] text-slate-400">Saving never changes the balance, receipts or payments. Unpaid rows can be edited any time.</p>
    </div>
  );
};
